/**
 * Level9Scene - 关卡9：安培定律
 * @description 教学目标：理解电流产生磁场
 * 玩家调节电流强度，观察磁场变化，使用右手定则判断磁场方向
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { EMField } from '@/physics/EMField.ts';
import { FieldRenderer } from '@/physics/FieldRenderer.ts';
import type { MagneticSource, ChargedParticle } from '@/physics/EMField.ts';

// 关卡9特定配置
interface Level9SpecificConfig {
  wirePosition: { x: number; y: number };
  initialCurrent: number;
  targetCurrent: number;
  currentTolerance: number;
  magneticScaleFactor: number;
  formula: string;
  hint: string;
  rightHandRule: string;
  // 目标区域配置（用于粒子到达检测）
  goal?: {
    position: { x: number; y: number };
  };
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level9Scene 类
 * 关卡9场景，实现安培定律教学：电流产生磁场
 */
export class Level9Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level9SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 电磁场系统
  private emField: EMField | null = null;
  private fieldRenderer: FieldRenderer | null = null;

  // 磁场源（电流导线）
  private magneticSourceId: string = 'current-wire';
  private currentStrength: number = 0;

  // 带电粒子（用于展示磁场效果）
  private particle: ChargedParticle | null = null;
  private particleGraphics: Phaser.GameObjects.Graphics | null = null;

  // 游戏对象
  private wireGraphics: Phaser.GameObjects.Graphics | null = null;
  private compassGraphics: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private currentSlider: Phaser.GameObjects.Graphics | null = null;
  private currentValueText: Phaser.GameObjects.Text | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 罗盘位置数组（用于可视化磁场方向）
  private compassPositions: Array<{ x: number; y: number; angle: number }> = [];

  constructor() {
    super({ key: 'Level9Scene' });
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-9', 'src/levels/levels/level-9.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-9') as LevelConfig;
    
    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level9SpecificConfig;
    }

    // 初始化电磁场系统
    this.initEMField();

    // 创建背景
    this.createBackground();

    // 创建电流导线
    this.createWire();

    // 创建罗盘阵列
    this.createCompassArray();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 初始渲染磁场
    this.updateMagneticField();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;

    // 更新带电粒子运动
    if (this.particle && this.emField) {
      this.particle = this.emField.updateParticleMotion(this.particle, delta / 1000);
      this.renderParticle();
      
      // 检测粒子是否到达目标区域
      this.checkParticlePosition();
    }

    // 更新罗盘方向
    this.updateCompasses();

    // 渲染磁场
    if (this.fieldRenderer) {
      this.fieldRenderer.render();
    }
  }

  /**
   * 初始化电磁场系统
   */
  private initEMField(): void {
    const scaleFactor = this.levelSpecific?.magneticScaleFactor ?? 1e-4;
    this.emField = new EMField(scaleFactor);

    // 创建磁场渲染器
    // 注意：物理坐标 y 向上，屏幕坐标 y 向下
    this.fieldRenderer = new FieldRenderer(
      this,
      this.emField,
      {
        magneticFieldColor: 0x9b59b6,
        lineWidth: 2,
        fieldLineCount: 8,
      },
      GAME_HEIGHT,
      1
    );
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深色背景
    this.cameras.main.setBackgroundColor('#1a1a2e');

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x2a2a4a, 0.3);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 创建电流导线（垂直导线）
   */
  private createWire(): void {
    const wireX = this.levelSpecific?.wirePosition?.x ?? 400;
    const wireY = this.levelSpecific?.wirePosition?.y ?? 300;

    this.wireGraphics = this.add.graphics();
    this.wireGraphics.setDepth(10);

    // 绘制导线（垂直线）
    this.wireGraphics.lineStyle(6, 0xf39c12);
    this.wireGraphics.beginPath();
    this.wireGraphics.moveTo(wireX, 50);
    this.wireGraphics.lineTo(wireX, 550);
    this.wireGraphics.strokePath();

    // 导线两端标记
    this.wireGraphics.fillStyle(0xe74c3c, 1);
    this.wireGraphics.fillCircle(wireX, 50, 10); // 上端（电流入口）

    this.wireGraphics.fillStyle(0x3498db, 1);
    this.wireGraphics.fillCircle(wireX, 550, 10); // 下端（电流出口）

    // 电流方向标注
    this.add.text(wireX + 20, 50, 'I ↑', {
      fontSize: '14px',
      color: '#e74c3c',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    // 导线标签
    this.add.text(wireX - 60, wireY, '电流导线', {
      fontSize: '14px',
      color: '#f39c12',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建罗盘阵列（可视化磁场方向）
   */
  private createCompassArray(): void {
    const wireX = this.levelSpecific?.wirePosition?.x ?? 400;
    const wireY = this.levelSpecific?.wirePosition?.y ?? 300;

    // 在导线周围创建罗盘阵列（同心圆分布）
    const radii = [80, 150, 220];
    const compassCountPerRing = [4, 6, 8];

    for (let ringIndex = 0; ringIndex < radii.length; ringIndex++) {
      const radius = radii[ringIndex]!;
      const count = compassCountPerRing[ringIndex]!;
      const angleStep = (2 * Math.PI) / count;

      for (let i = 0; i < count; i++) {
        const angle = i * angleStep;
        const x = wireX + radius * Math.cos(angle);
        const y = wireY - radius * Math.sin(angle); // 注意屏幕坐标翻转

        this.compassPositions.push({
          x,
          y,
          angle: 0, // 初始角度，后续根据磁场方向更新
        });

        // 绘制罗盘标记
        this.drawCompassMarker(x, y, 0);
      }
    }

    this.compassGraphics = this.add.graphics();
    this.compassGraphics.setDepth(9);
  }

  /**
   * 绘制单个罗盘标记
   */
  private drawCompassMarker(x: number, y: number, rotationAngle: number): void {
    if (!this.compassGraphics) return;

    const size = 15;
    
    this.compassGraphics.save();
    this.compassGraphics.translateCanvas(x, y);
    this.compassGraphics.rotateCanvas(rotationAngle);

    // 罗盘背景
    this.compassGraphics.fillStyle(0x2c3e50, 0.8);
    this.compassGraphics.fillCircle(0, 0, size);

    // N极指针（红色）
    this.compassGraphics.fillStyle(0xe74c3c, 1);
    this.compassGraphics.beginPath();
    this.compassGraphics.moveTo(0, -size * 0.6);
    this.compassGraphics.lineTo(-3, 0);
    this.compassGraphics.lineTo(3, 0);
    this.compassGraphics.closePath();
    this.compassGraphics.fillPath();

    // S极指针（蓝色）
    this.compassGraphics.fillStyle(0x3498db, 1);
    this.compassGraphics.beginPath();
    this.compassGraphics.moveTo(0, size * 0.6);
    this.compassGraphics.lineTo(-3, 0);
    this.compassGraphics.lineTo(3, 0);
    this.compassGraphics.closePath();
    this.compassGraphics.fillPath();

    this.compassGraphics.restore();
  }

  /**
   * 更新罗盘方向（根据磁场方向）
   */
  private updateCompasses(): void {
    if (!this.compassGraphics || !this.emField) return;

    const wireX = this.levelSpecific?.wirePosition?.x ?? 400;
    const wireY = this.levelSpecific?.wirePosition?.y ?? 300;

    this.compassGraphics.clear();

    for (const compass of this.compassPositions) {
      // 计算该点的磁场方向
      // 根据安培定律，磁场方向垂直于导线和到该点的连线
      // 右手定则：拇指指向电流方向（向上），四指弯曲方向为磁场方向
      
      const dx = compass.x - wireX;
      const dy = wireY - compass.y; // 注意坐标翻转
      
      // 磁场方向角度（相对于罗盘位置）
      // 根据右手定则，磁场方向为顺时针方向（电流向上）
      const tangentAngle = Math.atan2(dx, dy) + Math.PI / 2;
      
      // 磁场强度影响罗盘转向速度
      const strengthFactor = Math.abs(this.currentStrength) / 10;
      compass.angle = tangentAngle * strengthFactor;

      this.drawCompassMarker(compass.x, compass.y, compass.angle);
    }
  }

  /**
   * 更新磁场（根据电流强度）
   */
  private updateMagneticField(): void {
    if (!this.emField) return;

    // 移除旧的磁场源
    this.emField.removeMagneticSource(this.magneticSourceId);

    // 创建新的磁场源（电流导线）
    const wireX = this.levelSpecific?.wirePosition?.x ?? 400;
    const wireY = this.levelSpecific?.wirePosition?.y ?? 300;

    // 磁场源强度与电流成正比
    const magneticSource: MagneticSource = {
      id: this.magneticSourceId,
      position: Vec2(wireX, wireY),
      strength: this.currentStrength,
      radius: 250, // 磁场影响范围
    };

    this.emField.addMagneticSource(magneticSource);

    // 更新渲染
    if (this.fieldRenderer) {
      this.fieldRenderer.render();
    }

    // 更新罗盘
    this.updateCompasses();

    // 更新电流显示
    if (this.currentValueText) {
      this.currentValueText.setText(`I = ${this.currentStrength.toFixed(1)} A`);
    }

    // 更新提示
    if (this.hintText) {
      const targetCurrent = this.levelSpecific?.targetCurrent ?? 5;
      if (Math.abs(this.currentStrength - targetCurrent) < 0.5) {
        this.hintText.setText('电流强度正确！磁场强度达到目标值');
        this.hintText.setColor('#4ade80');
      } else {
        this.hintText.setText(this.levelSpecific?.hint ?? '调节电流强度，观察磁场变化');
        this.hintText.setColor('#888888');
      }
    }
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '安培定律：电流产生磁场', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式显示
    this.add.text(GAME_WIDTH / 2, 60, 'B = μ₀I / 2πr', {
      fontSize: '18px',
      color: '#f39c12',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 右手定则说明
    this.add.text(GAME_WIDTH / 2, 90, '右手定则：拇指指向电流方向，四指弯曲为磁场方向', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 120,
      this.levelSpecific?.hint ?? '调节电流强度，观察磁场变化', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 电流强度控制区域
    this.createCurrentControl();

    // 开始按钮
    this.createStartButton();

    // 粒子控制提示
    this.add.text(GAME_WIDTH / 2, 530, '点击"开始"发射带电粒子观察磁场效果', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 目标电流提示
    const targetCurrent = this.levelSpecific?.targetCurrent ?? 5;
    this.add.text(GAME_WIDTH / 2, 560, `目标电流: ${targetCurrent} A`, {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '32px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);
  }

  /**
   * 创建电流强度控制区域
   */
  private createCurrentControl(): void {
    // 控制区域背景
    const controlBg = this.add.graphics();
    controlBg.fillStyle(0x2a2a4a, 1);
    controlBg.fillRect(250, 480, 300, 40);
    controlBg.lineStyle(2, 0x4a4a6a);
    controlBg.strokeRect(250, 480, 300, 40);

    // 控制提示
    this.add.text(250, 460, '调节电流强度 (单位: A):', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 电流值显示
    this.currentValueText = this.add.text(400, 500, `I = ${this.currentStrength.toFixed(1)} A`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 滑块区域
    this.currentSlider = this.add.graphics();
    this.currentSlider.setDepth(11);

    // 增加电流按钮
    this.createButton(580, 500, '+', () => {
      if (this.gameState === 'idle') {
        this.currentStrength = Math.min(10, this.currentStrength + 0.5);
        this.updateMagneticField();
        this.updateSliderPosition();
      }
    });

    // 减少电流按钮
    this.createButton(220, 500, '-', () => {
      if (this.gameState === 'idle') {
        this.currentStrength = Math.max(0, this.currentStrength - 0.5);
        this.updateMagneticField();
        this.updateSliderPosition();
      }
    });

    // 预设电流按钮
    this.createPresetButtons();

    // 初始滑块位置
    this.updateSliderPosition();
  }

  /**
   * 更新滑块位置
   */
  private updateSliderPosition(): void {
    if (!this.currentSlider) return;

    this.currentSlider.clear();

    // 滑块轨道
    this.currentSlider.lineStyle(2, 0x4a4a6a);
    this.currentSlider.beginPath();
    this.currentSlider.moveTo(270, 500);
    this.currentSlider.lineTo(530, 500);
    this.currentSlider.strokePath();

    // 滑块位置（电流范围 0-10，映射到 270-530）
    const sliderX = 270 + (this.currentStrength / 10) * 260;

    // 滑块手柄
    this.currentSlider.fillStyle(0xf39c12, 1);
    this.currentSlider.fillCircle(sliderX, 500, 8);
    this.currentSlider.lineStyle(2, 0xffffff);
    this.currentSlider.strokeCircle(sliderX, 500, 8);
  }

  /**
   * 创建预设电流按钮
   */
  private createPresetButtons(): void {
    const presets = [2, 5, 8];
    const targetCurrent = this.levelSpecific?.targetCurrent ?? 5;

    presets.forEach((current, i) => {
      const x = 100 + i * 100;
      const y = 450;
      const isTarget = current === targetCurrent;

      const btn = this.add.graphics();
      btn.fillStyle(isTarget ? 0x4ade80 : 0x4a4a6a, 1);
      btn.fillRect(x - 35, y - 15, 70, 30);
      btn.lineStyle(1, isTarget ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRect(x - 35, y - 15, 70, 30);

      this.add.text(x, y, `${current}A`, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);

      // 点击事件
      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 35, y - 15, 70, 30),
        Phaser.Geom.Rectangle.Contains
      );
      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.currentStrength = current;
          this.updateMagneticField();
          this.updateSliderPosition();
        }
      });

      // hover 效果
      btn.on('pointerover', () => {
        btn.clear();
        btn.fillStyle(isTarget ? 0x5aee90 : 0x5a5a7a, 1);
        btn.fillRect(x - 35, y - 15, 70, 30);
        btn.strokeRect(x - 35, y - 15, 70, 30);
      });

      btn.on('pointerout', () => {
        btn.clear();
        btn.fillStyle(isTarget ? 0x4ade80 : 0x4a4a6a, 1);
        btn.fillRect(x - 35, y - 15, 70, 30);
        btn.strokeRect(x - 35, y - 15, 70, 30);
      });
    });
  }

  /**
   * 创建通用按钮
   */
  private createButton(x: number, y: number, label: string, callback: () => void): Phaser.GameObjects.Graphics {
    const btn = this.add.graphics();
    btn.fillStyle(0x4a4a6a, 1);
    btn.fillRect(x - 25, y - 15, 50, 30);
    btn.lineStyle(1, 0x6a6a8a);
    btn.strokeRect(x - 25, y - 15, 50, 30);

    this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    btn.setInteractive(
      new Phaser.Geom.Rectangle(x - 25, y - 15, 50, 30),
      Phaser.Geom.Rectangle.Contains
    );
    btn.on('pointerdown', callback);

    btn.on('pointerover', () => {
      btn.clear();
      btn.fillStyle(0x5a5a7a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    btn.on('pointerout', () => {
      btn.clear();
      btn.fillStyle(0x4a4a6a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    return btn;
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(650, 480, 100, 40);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(650, 480, 100, 40);

    this.add.text(700, 500, '开始', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(650, 480, 100, 40),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startSimulation());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(650, 480, 100, 40);
      this.startButton?.strokeRect(650, 480, 100, 40);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(650, 480, 100, 40);
      this.startButton?.strokeRect(650, 480, 100, 40);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    // 键盘输入
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'ArrowUp' || event.key === 'w') {
        this.currentStrength = Math.min(10, this.currentStrength + 0.5);
        this.updateMagneticField();
        this.updateSliderPosition();
      }

      if (event.key === 'ArrowDown' || event.key === 's') {
        this.currentStrength = Math.max(0, this.currentStrength - 0.5);
        this.updateMagneticField();
        this.updateSliderPosition();
      }

      if (event.key === 'Enter') {
        this.startSimulation();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetSimulation();
      }
    });
  }

  /**
   * 开始模拟（发射带电粒子）
   */
  private startSimulation(): void {
    if (this.gameState !== 'idle') return;
    if (this.currentStrength <= 0) {
      this.hintText?.setText('请先设置电流强度！');
      return;
    }

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 创建带电粒子
    const wireX = this.levelSpecific?.wirePosition?.x ?? 400;
    const wireY = this.levelSpecific?.wirePosition?.y ?? 300;

    // 粒子从导线附近开始，有一定的初始速度
    this.particle = {
      id: 'test-particle',
      position: Vec2(wireX + 100, wireY),
      velocity: Vec2(0, 50), // 初始向上运动
      charge: 1, // 正电荷
      mass: 1,
    };

    // 创建粒子渲染图形
    this.particleGraphics = this.add.graphics();
    this.particleGraphics.setDepth(12);

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新按钮状态
    this.updateStartButtonState('running');
  }

  /**
   * 渲染粒子
   */
  private renderParticle(): void {
    if (!this.particleGraphics || !this.particle) return;

    this.particleGraphics.clear();

    // 粒子颜色（根据电荷）
    const color = this.particle.charge > 0 ? 0xff6b6b : 0x4ecdc4;

    // 绘制粒子
    this.particleGraphics.fillStyle(color, 1);
    this.particleGraphics.fillCircle(
      this.particle.position.x,
      GAME_HEIGHT - this.particle.position.y, // 坐标翻转
      12
    );
    this.particleGraphics.lineStyle(2, 0xffffff);
    this.particleGraphics.strokeCircle(
      this.particle.position.x,
      GAME_HEIGHT - this.particle.position.y,
      12
    );

    // 绘制速度向量
    const velocityScale = 0.5;
    const vx = this.particle.velocity.x * velocityScale;
    const vy = -this.particle.velocity.y * velocityScale; // 坐标翻转

    this.particleGraphics.lineStyle(2, 0xffff00);
    this.particleGraphics.beginPath();
    this.particleGraphics.moveTo(
      this.particle.position.x,
      GAME_HEIGHT - this.particle.position.y
    );
    this.particleGraphics.lineTo(
      this.particle.position.x + vx,
      GAME_HEIGHT - this.particle.position.y + vy
    );
    this.particleGraphics.strokePath();

    // 添加电荷标签
    this.particleGraphics.fillStyle(0xffffff, 1);
  }

  /**
   * 检测粒子位置（判断是否成功）
   */
  private checkParticlePosition(): void {
    if (!this.particle) return;

    const goalX = this.levelSpecific?.goal?.position?.x ?? 600;
    const goalY = this.levelSpecific?.goal?.position?.y ?? 400;
    const goalSize = 60;

    // 检测是否到达目标区域
    const screenX = this.particle.position.x;
    const screenY = GAME_HEIGHT - this.particle.position.y;

    if (
      screenX >= goalX - goalSize / 2 &&
      screenX <= goalX + goalSize / 2 &&
      screenY >= goalY - goalSize / 2 &&
      screenY <= goalY + goalSize / 2
    ) {
      this.gameState = 'success';
      this.showSuccessResult();
      this.updateStartButtonState('idle');
    }

    // 检测是否超出边界
    if (
      this.particle.position.x < 0 ||
      this.particle.position.x > GAME_WIDTH ||
      this.particle.position.y < 0 ||
      this.particle.position.y > GAME_HEIGHT
    ) {
      this.gameState = 'failed';
      this.showFailResult('粒子超出边界！');
      this.updateStartButtonState('idle');
    }
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
      this.startButton.fillRect(650, 480, 100, 40);
    } else if (state === 'idle') {
      this.startButton.fillStyle(0x4ade80, 1);
      this.startButton.fillRect(650, 480, 100, 40);
    }
    this.startButton.strokeRect(650, 480, 100, 40);
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(9, this.elapsedTime);

    this.resultText?.setText(`🎉 成功！用时 ${this.elapsedTime.toFixed(2)}秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(`安培定律：电流 I=${this.currentStrength}A 产生磁场`);

    this.createCompletionButtons();
  }

  /**
   * 创建完成按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 80;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(10)) {
        this.scene.start('Level10Scene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText(`调整电流强度，使粒子到达目标区域`);
  }

  /**
   * 重置模拟
   */
  private resetSimulation(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;

    // 清除粒子
    this.particle = null;
    if (this.particleGraphics) {
      this.particleGraphics.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '调节电流强度，观察磁场变化');
    this.hintText?.setColor('#888888');

    // 更新按钮状态
    this.updateStartButtonState('idle');
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-9');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 9,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前电流强度
   */
  getCurrentStrength(): number {
    return this.currentStrength;
  }
}